import * as Sentry from '@sentry/react'
import { LogLevel } from '@messenger/consts'
import type { LogData, LogLevelType } from '@messenger/types'

const isDevelopment = process.env.REACT_APP_ENV === 'development'

/**
 * Formats log data for Sentry with consistent structure
 */
const formatLogForSentry = (level: LogLevelType, data: LogData) => {
  const { error, message, category, ...rest } = data

  // Extract error details if present
  const errorDetails = error
    ? {
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
      }
    : undefined

  return {
    message,
    level,
    tags: {
      category,
      environment: process.env.REACT_APP_ENV || 'development',
    },
    extra: {
      timestamp: new Date().toISOString(),
      logLevel: level,
      errorDetails,
      ...rest,
    },
  }
}

/**
 * Sends log messages to Sentry based on log level
 */
const logMessageToSentry = (level: LogLevelType, data: LogData) => {
  // Skip logging in development environment
  if (isDevelopment) return

  const { message, error } = data
  const formattedLog = formatLogForSentry(level, data)

  switch (level) {
    case LogLevel.ERROR:
      if (error) {
        // Send error with full context
        Sentry.withScope((scope) => {
          scope.setLevel('error')
          scope.setTags(formattedLog.tags)
          scope.setExtras(formattedLog.extra)
          Sentry.captureException(error)
        })
      } else {
        // Send error message
        Sentry.captureMessage(message, {
          level: 'error',
          tags: formattedLog.tags,
          extra: formattedLog.extra,
        })
      }
      break

    case LogLevel.WARN:
      Sentry.captureMessage(message, {
        level: 'warning',
        tags: formattedLog.tags,
        extra: formattedLog.extra,
      })
      break

    case LogLevel.INFO:
      Sentry.captureMessage(message, {
        level: 'info',
        tags: formattedLog.tags,
        extra: formattedLog.extra,
      })
      break

    case LogLevel.DEBUG:
      Sentry.captureMessage(message, {
        level: 'debug',
        tags: formattedLog.tags,
        extra: formattedLog.extra,
      })
      break
  }
}

/**
 * Sentry logger utility with same interface as the Datadog logger
 * Provides structured logging to Sentry for error tracking and debugging
 */
export const loggerSentry = {
  error: (data: LogData) => logMessageToSentry(LogLevel.ERROR, data),
  warn: (data: LogData) => logMessageToSentry(LogLevel.WARN, data),
  info: (data: LogData) => logMessageToSentry(LogLevel.INFO, data),
  debug: (data: LogData) => logMessageToSentry(LogLevel.DEBUG, data),
}

// Export the constants and types for convenience
export { LogLevel, EventCategory } from '@messenger/consts'
export type { LogData, LogLevelType, EventCategoryType } from '@messenger/types'
